<template>
  <a-modal
    v-if="visible"
    id="model-version-modal"
    centered
    destroy-on-close
    style="width: 40%"
    title="Model Versions"
    :visible="true"
    :mask-closable="false"
    @ok="handleOk"
    @cancel="closeModal"
  >
    <a-space size="40" direction="vertical" class="w-100">
      <a-typography-title :level="4" class="ml-2 mt-2">
        {{ modelChoice ? modelChoice : '' }}
      </a-typography-title>
      <a-table
        class="mt-3"
        :columns="ModelVersionAiAnnotationColumns"
        :data-source="filteredModelVersionsList"
        :row-selection="rowSelection"
        :row-key="'id'"
        :scroll="{ y: '40vh', x: true }"
        :pagination="false"
      >
        <template #emptyText>
          <div
            id="no-models-found"
            style="height: 35vh"
            class="d-flex flex-column align-items-center justify-content-center"
          >
            <laptop-outlined style="font-size: 40px" />
            <span class="mt-2">No Models Found</span>
          </div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column.dataIndex === 'description'">
            {{ record.description }}
          </template>

          <template v-if="column.dataIndex === 'date'">
            {{ record.date ? dateHelper.formatDate(record.date) : '-' }}
          </template>

          <template v-if="column.dataIndex === 'version_number'">
            {{ record.version_number }}
          </template>
        </template>
      </a-table>
    </a-space>
    <template #footer>
      <div class="d-flex justify-content-end">
        <a-button class="mr-2" @click="closeModal">Cancel</a-button>
        <a-button
          type="primary"
          :disabled="isProceedButtonDisabled"
          @click="handleOk"
        >
          Submit
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { LaptopOutlined } from '@ant-design/icons-vue';
import { integer } from '@vuelidate/validators';
import dayjs from 'dayjs';
import dateHelper from 'src/components/shared/Helpers/dateHelper';
import {
  ModelVersionAiAnnotationColumns,
  modelTypeSelection,
  taskObjectListColumns,
} from 'src/components/user-panel/pages/process/ObjectDetection/config';
import { longRunningTaskStatus } from 'src/components/user-panel/pages/process/ObjectDetection/config.js';
import LongRunningTaskService from 'src/services/longRunningTask';
import orgServices from 'src/services/organization';
import SQSServices from 'src/services/sqs';
import taskServices from 'src/services/tasks';
import { uuid } from 'vue-uuid';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    LaptopOutlined,
  },
  inject: ['toast'],
  props: {
    visible: Boolean,
    taskId: integer,
    groupId: integer,
  },
  emits: ['update:visible'],

  setup(props, { emit }) {
    const closeModal = () => {
      console.log('closing...');
      emit('update:visible', false);
    };
    return {
      ModelVersionAiAnnotationColumns,
      taskObjectListColumns,
      dateHelper,
      modelTypeSelection,
      closeModal,
    };
  },

  data() {
    return {
      selectedRow: null,
      selectedModelTypeObject: [],
      modelChoiceId: null,
      modelChoice: null,
      selectedObjects: [],
      selectObjectsName: [],
      taskObjects: [],
      TaskObjectColumns: [
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
        },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'modelVersionsList',
      'taskName',
      'selectedTask',
      'organization',
    ]),

    filteredModelVersionsList() {
      return this.modelVersionsList.filter(
        (mv) => !mv.is_deleted && mv.model_type === this.modelChoiceId
      );
    },

    rowSelection() {
      return {
        type: 'radio',
        selectedRowKeys: [this.selectedRow],
        onChange: this.onRowSelect,
      };
    },

    isProceedButtonDisabled() {
      if (!this.selectedRow) {
        return true;
      }
      return false;
    },
  },

  watch: {
    async visible(value) {
      if (value) {
        await this.fetchModelVersions();
        const [err, data] = await taskServices.fetchTaskDetailsNew(this.taskId);
        this.modelChoiceId = data.model_type;
        this.modelChoice = data.model_choice;
      }
    },
  },

  methods: {
    ...mapActions(['fetchModelVersionsList']),
    async fetchModelVersions() {
      await this.fetchModelVersionsList(this.taskId);
      this.filteredModelVersionsList = this.filterByModelType(
        this.modelChoiceId
      );
    },

    filterByModelType(modelChoiceId) {
      if (!this.modelVersionsList.length) return [];
      const sorted = this.modelVersionsList
        .filter((mv) => !mv.is_deleted && mv.model_type === modelChoiceId)
        .sort((a, b) => (dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1));
      return sorted;
    },

    handleOk() {
      const ContainObject = this.selectedModelTypeObject.includes('objects');
      if (ContainObject) {
        if (this.step === 1) {
          this.proceedToObjectSelection();
        } else if (this.step === 2) {
          this.proceedToTaskObjectsSelection();
        } else {
          this.startValidation();
        }
      } else {
        this.startValidation();
      }
    },
    handleBack() {
      if (this.step > 1) {
        this.step -= 1; // Navigate back one step
      }
    },

    async startValidation() {
      // start validation process
      await this.sendMessageToQueue(this.groupId);
      this.closeModal();
    },

    async setModelVersion(groupId) {
      const [err, data] = await orgServices.fetchValidationVideosOfGroup(
        groupId
      );
      let payload = {
        model_version: this.selectedRow,
      };
      for (const validationVideo of data) {
        await orgServices.updateGroupValidateVideos(
          validationVideo.id,
          payload
        );
      }
    },

    async getLongRunningTaskId(record_id) {
      const payload = {
        name: 'group_validation_record',
        status: longRunningTaskStatus.PENDING,
        details: {
          progress: 0,
        },
        event: {
          group_id: record_id,
          file_name: '',
        },
        organization: this.organization,
        task: this.taskId,
      };

      const [error, data] = await LongRunningTaskService.createLongRunningTasks(
        payload
      );
      if (error) return null;
      return data.id;
    },

    getValidationQueuePayload(record_id, longRunningTaskId) {
      return {
        long_running_task_id: longRunningTaskId,
        group_id: record_id,
        run_id: uuid.v4(),
        type_of_run: 'batch',
        model_version: this.selectedRow,
      };
    },

    async createValidationRun(payload) {
      const [error] = await orgServices.createValidationRun(payload);
      return error ? false : true;
    },

    async sendMessageToQueue(record_id) {
      const longRunningTaskId = await this.getLongRunningTaskId(record_id);
      const messagePayload = this.getValidationQueuePayload(
        record_id,
        longRunningTaskId
      );
      const payload = {
        queueName: 'validation_queue',
        sqsMessage: messagePayload,
      };
      if (
        !(await this.createValidationRun({
          run_id: messagePayload.run_id,
          model_version: messagePayload.model_version,
          group: messagePayload.group_id,
        }))
      ) {
        return this.toast.error('Failed to create validation run record.');
      }
      const [error] = await SQSServices.sendSQSMessage(payload);
      if (error) {
        this.toast.error('Error occured while sending message to queue');
        return false;
      }
      this.toast.success('Group sent for validation successfully!');
      return true;
    },

    onRowSelect(selectedRowKeys) {
      this.selectedRow = selectedRowKeys[0];
    },
  },
};
</script>
